import FileMoveModal from "@app-components/modal/FileMoveModal";
import {
  arrangeNodes,
  NodeData,
  useLocalFiles,
} from "@app-utils/localStorage/localFiles";
import {
  EventKeyModifiers,
  FileExplorerEntryContextArguments,
} from "@enfusion-ui/core";
import { useRefCallback } from "@enfusion-ui/hooks";
import { FileSortOption, FileTreeEntry } from "@enfusion-ui/types";
import { getFileIcon, getFileName } from "@enfusion-ui/utils";
import {
  AccordionContext,
  Button,
  ButtonProps,
  CenterContent,
  CreateFolderModal,
  DateFilterForm,
  ErrorAlert,
  FileExplorer,
  FilterDateType,
  MenuOptions,
  PositionedPortal,
  Spinner,
  TextInput,
  useConfirmationModal,
  useNavBarState,
} from "@enfusion-ui/web-components";
import {
  errorToast,
  FolderActionContext,
  gaModalView,
  styled,
  successToast,
  useContextMenu,
  useTabs,
} from "@enfusion-ui/web-core";
import {
  faBroom,
  faCheck,
  faDesktop,
  faFileImport,
  faFilter,
  faFolderPlus,
  faShare,
  faTrash,
  IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { sortBy } from "lodash";
import Menu, { Divider, MenuItem, SubMenu } from "rc-menu";
import { MenuInfo } from "rc-menu/lib/interface";
import * as React from "react";
import { useDebounce } from "react-use";
import { v4 as uuidv4 } from "uuid";

const StyledButton = styled(Button)<{ isFiltered: boolean }>`
  &&& {
    height: 38px;
    color: ${({ isFiltered }) => {
      return isFiltered ? "var(--primary)" : "var(--text-normal)";
    }};
  }
`;
const StyledIcon = styled(FontAwesomeIcon)`
  margin-right: var(--spacing-l);
`;
export const FilterButton = React.forwardRef<
  HTMLButtonElement,
  ButtonProps & { isFiltered: boolean; icon?: IconDefinition }
>(function FilterButton(props, ref) {
  const { icon, ...rest } = props;
  return (
    <StyledButton ref={ref} {...rest}>
      <FontAwesomeIcon icon={icon ? icon : faFilter} />
    </StyledButton>
  );
});

export type DateFilter = {
  dateType: FilterDateType;
  dateRange: [Date | null, Date | null];
};

const ServicesFileExplorer = styled(FileExplorer)`
  flex: 1;
  overflow-x: hidden;
`;

const SearchPanel = styled.div`
  padding: 0.5rem;
  background-color: var(--background-accent);
  margin-bottom: var(--spacing);
`;

const SearchForm = styled.form`
  display: flex;
`;

const FilterIconContainer = styled.div`
  height: 38px;
  margin-left: 4px;
  align-self: flex-end;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DateFilterContent = styled.div`
  display: ${({ open }: { open: boolean }) => (open ? "block" : "none")};
`;

function notValid(value?: Date | null) {
  return value === null || typeof value === "undefined";
}

const DirectoryExplorer: React.FC<{
  open: boolean;
}> = React.memo(function DirectoryExplorer() {
  const { openTab } = useTabs();
  const {
    files: nodes,
    filteredNodes,
    error,
    loading,
    deleteFolder,
    deleteFile,
    moveFile,
    createFolder,
    retrieveNodes,
    setParams,
    deleteAll,
    savedRequests,
    setSavedRequests,
  } = useLocalFiles();

  const { closeNavBarTabOnMobile } = useNavBarState();
  const { setMenu, setSearch, setForceSearchIcon } =
    React.useContext(AccordionContext);

  const [sortOption, setSortOption] =
    React.useState<FileSortOption>("ModifiedAt");
  const [searchTerm, setSearchTerm] = React.useState("");
  const [dateFilterOpen, setDateFilterOpen] = React.useState(false);
  const [dateFilter, setDateFilter] = React.useState<DateFilter | null>(null);
  const [importedRequest, setImportedRequest] = React.useState<string>("");

  const [createFolderModalOpen, setCreateFolderModalOpen] =
    React.useState(false);
  const [moveFileModalOpen, setMoveFileModalOpen] = React.useState(false);

  const [filePath, setFilePath] = React.useState("");
  const [folderPath, setFolderPath] = React.useState("");

  const [selectedValues, setSelectedValues] = React.useState<string[]>([]);
  const [allNodesSelected, setAllNodesSelected] = React.useState(false);
  const [foldersOpen, setFoldersOpen] = React.useState<boolean>(false);

  const duplicateRequests = React.useRef<Array<NodeData>>([]);
  const selectedNodes = React.useRef<NodeData[]>([]);
  const haveSelectedNodes = selectedValues?.length >= 1;

  const {
    contextMenuOpen,
    contextMenuNode,
    handleContextMenuPlacement,
    closeContextMenu,
    setContextMenuNode,
    openContextMenu,
  } = useContextMenu<{
    node: NodeData | null;
    clientY: number | null;
    clientX: number | null;
  }>();

  React.useEffect(() => {
    if (searchTerm.trim().length > 0 || dateFilter) {
      setForceSearchIcon(true);
    } else {
      setForceSearchIcon(false);
    }
  }, [searchTerm, dateFilter]);

  //#region Handle accordion action interactions
  const sortHandler =
    (sortOption: FileSortOption) =>
    ({ domEvent }: MenuInfo) => {
      domEvent.preventDefault();
      domEvent.stopPropagation();
      setSortOption(sortOption);
    };

  const handleSearchChange = useRefCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    [searchTerm]
  );

  const handleDateFilterIconClick = useRefCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDateFilterOpen(!dateFilterOpen);
    },
    [dateFilterOpen]
  );

  const handleSetDateFilter = useRefCallback(
    (dateType: FilterDateType, dateRange: [Date | null, Date | null]) => {
      if (notValid(dateRange[0]) && notValid(dateRange[1])) {
        setDateFilter(null);
      } else {
        setDateFilter({ dateType, dateRange });
      }
    },
    [dateFilter]
  );

  const handleClearSearchTerm = () => {
    setSearchTerm("");
  };

  const openNewTab = () => {
    openTab({
      component: "inspector",
      name: "New Tab",
      icon: faDesktop,
      unique: uuidv4(),
    });
  };

  const openCreateFolder = () => {
    const node = contextMenuNode?.node as unknown as FileTreeEntry;
    const path = node?.path?.substring(0, node.path.lastIndexOf("/") + 1) ?? "";
    setCreateFolderModalOpen(true);
    setFolderPath(path);
    closeContextMenu();
  };

  const changeFileSelectionState = useRefCallback(
    (menuInfo: MenuInfo) => {
      setAllNodesSelected((prev) => !prev);
      if (menuInfo.key === "select") {
        const ids = nodes.map((node) => node.id);
        setSelectedValues(ids);
      } else {
        setSelectedValues([]);
      }
    },
    [nodes]
  );

  React.useEffect(() => {
    const adminActions: MenuOptions = [
      {
        key: "createFolder",
        title: "Create Folder",
        icon: faFolderPlus,
        onClick: openCreateFolder,
      },
      "divider",
    ];

    const actions: MenuOptions = [
      {
        key: "new-tab",
        title: "New Tab",
        onClick: openNewTab,
        icon: faDesktop,
      },
      {
        key: "import",
        title: "Import JSON",
        onClick: ImportConfirmation.openModal,
        icon: faFileImport,
      },

      {
        key: "sort",
        title: "Sort",
        children: [
          {
            key: "Name",
            title: "Sort by Name",
            onClick: sortHandler("Name"),
          },
          {
            key: "ModifiedAt",
            title: "Sort by Modified At",
            onClick: sortHandler("ModifiedAt"),
          },
          {
            key: "CreatedAt",
            title: "Sort by Created At",
            onClick: sortHandler("CreatedAt"),
          },
        ],
      },
    ];

    if (!nodes.length) {
      setAllNodesSelected(false);
    }

    if (nodes.length) {
      actions.splice(2, 0, {
        key: allNodesSelected ? "clear" : "select",
        title: allNodesSelected ? "Clear All" : "Select All",
        onClick: changeFileSelectionState,
        icon: allNodesSelected ? faBroom : faCheck,
      });
    }

    setMenu({
      menu: [...adminActions, ...actions],
      selectedKeys: [sortOption],
    });
  }, [allNodesSelected, sortOption]);

  React.useEffect(() => {
    setSearch(
      <SearchPanel>
        <SearchForm>
          <TextInput
            label="Search"
            name="name"
            value={searchTerm}
            onChange={handleSearchChange}
            clearable
            onClearValue={handleClearSearchTerm}
          />
          <FilterIconContainer>
            <FilterButton
              title="Date Filter"
              onClick={handleDateFilterIconClick}
              isFiltered={dateFilter !== null}
            />
          </FilterIconContainer>
        </SearchForm>
        <DateFilterContent open={dateFilterOpen}>
          <DateFilterForm onChange={handleSetDateFilter} />
        </DateFilterContent>
      </SearchPanel>
    );
  }, [dateFilterOpen, dateFilter, searchTerm]);
  //#endregion

  //#region FileExplorer handlers
  useDebounce(
    () => {
      const [fromDate, toDate] = dateFilter?.dateRange || [];
      setParams({
        sortOption,
        query: searchTerm?.trim(),
        ascending: sortOption === "Name",
        fromDate,
        toDate,
        dateFilter,
      });
    },
    500,
    [searchTerm, sortOption, dateFilter]
  );

  React.useEffect(() => {
    if ((searchTerm?.trim().length > 0 || dateFilter) && !foldersOpen) {
      setFoldersOpen(true);
    }

    if (searchTerm.trim().length === 0 && !dateFilter) {
      setFoldersOpen(false);
    }
  }, [nodes, searchTerm, dateFilter]);

  const handleClick = useRefCallback(
    (node: NodeData, modifiers: EventKeyModifiers) => {
      if (node.file && !modifiers.ctrlKey) {
        const fileName: string = node.name;
        openTab({
          component: "file",
          name: fileName,
          unique: `${node.name}`,
          icon: getFileIcon(node),
          config: node.config,
        });
        closeNavBarTabOnMobile();
      }
    },
    [closeNavBarTabOnMobile, nodes]
  );
  //#endregion

  //#region Handle context menu
  const closeFileMoveModal = () => {
    setMoveFileModalOpen(false);
    closeContextMenu();
  };

  const openFileMoveModal = () => {
    const node = contextMenuNode?.node as unknown as FileTreeEntry;
    const path = node.path.substring(0, node.path.lastIndexOf("/") + 1) ?? "/";

    gaModalView("/services/move-file");

    setFilePath(path);
    setMoveFileModalOpen(true);
    closeContextMenu();
  };

  const closeCreateFolder = () => {
    setCreateFolderModalOpen(false);
    closeContextMenu();
  };

  const handleCreateFolder = useRefCallback(
    (folderName: string, folderPath: string) => {
      createFolder(folderName, folderPath, undefined, true);
      closeCreateFolder();
    },
    [nodes]
  );

  const handleDeleteFile = useRefCallback(
    (node: NodeData | null) => {
      if (node) deleteFile(node, true);
    },
    [contextMenuNode]
  );

  const handleDeleteFolder = useRefCallback(
    (node: NodeData | null) => {
      if (node) deleteFolder(node, true);
    },
    [selectedNodes, contextMenuNode]
  );

  const handleEntryContext = useRefCallback(
    ({ node, clientX, clientY }: FileExplorerEntryContextArguments) => {
      setContextMenuNode({ node, clientX, clientY });
      openContextMenu();
    },
    []
  );

  const handleSelectionChange = useRefCallback((nodes: Array<NodeData>) => {
    selectedNodes.current = nodes;
  }, []);

  const handleClearSelectedFiles = useRefCallback(() => {
    setSelectedValues([]);
    setAllNodesSelected((prev) => !prev);
  }, []);

  const handleShareFile = useRefCallback(() => {
    try {
      if (selectedNodes.current.length) {
        navigator.clipboard.writeText(JSON.stringify(selectedNodes.current));
        successToast("Copied JSON of the files");
      } else {
        navigator.clipboard.writeText(JSON.stringify([contextMenuNode?.node]));
        successToast("Copied JSON of the file");
      }
    } catch (error) {
      errorToast("Failed to copy JSON");
    }

    closeContextMenu();
  }, [contextMenuNode]);

  const updateRequest = useRefCallback(
    async (node: NodeData) => {
      setSavedRequests((p) => ({ ...p, [node.name]: node }));
      duplicateRequests.current.splice(0, 1);
      successToast("Successfully modified request");
    },
    [savedRequests, duplicateRequests.current]
  );

  const handleImportRequests = useRefCallback(() => {
    const requestNodes: Array<NodeData> = JSON.parse(importedRequest);
    duplicateRequests.current = [];
    const sortedNodes = requestNodes.sort((a) => (a.file ? 1 : -1));
    const [duplicateNodes, nonDuplicateNodes] = sortedNodes.reduce(
      (res, node) => {
        const request = savedRequests[node.name];
        if (request) {
          res[0].push(node);
        } else {
          res[1][node.name] = { ...node, path: `/${node.name}` };
        }
        return res;
      },
      [[], {}] as [NodeData[], Record<string, NodeData>]
    );
    if (nonDuplicateNodes) {
      setSavedRequests((p) => ({ ...p, ...nonDuplicateNodes }));
      successToast("Successfully imported requests");
    }
    ImportConfirmation.closeModal();
    duplicateRequests.current = duplicateNodes;
  }, [savedRequests, importedRequest]);

  const handleDuplicateNameRequests = useRefCallback(async () => {
    if (duplicateRequests.current.length) {
      await updateRequest(duplicateRequests.current[0]);
    }
  }, [duplicateRequests.current]);

  const handleDeleteAll = useRefCallback(() => {
    deleteAll(true);
  }, []);

  const handleFileMove = useRefCallback(
    async (destinationFolder: NodeData | null) => {
      if (contextMenuNode && contextMenuNode.node && destinationFolder) {
        await moveFile(contextMenuNode.node, destinationFolder);
        deleteFile(contextMenuNode.node);
      }
      closeFileMoveModal();
    },

    [contextMenuNode, selectedNodes, savedRequests]
  );

  const handleSortNodes = useRefCallback(
    (nodes: Array<NodeData>): Array<NodeData> => {
      const res = sortBy(nodes, (node: FileTreeEntry) => {
        if (sortOption === "Name") return node.name;

        if (sortOption === "CreatedAt")
          return node.creationDateTime
            ? new Date(node.creationDateTime).valueOf()
            : Infinity;

        return node.modifiedDateTime
          ? new Date(node.modifiedDateTime).valueOf()
          : Infinity;
      }) as Array<NodeData>;

      return arrangeNodes(sortOption !== "Name" ? res.reverse() : res);
    },
    [sortOption]
  );

  const ImportConfirmation = useConfirmationModal({
    title: "Import Request",
    renderContent: () => (
      <TextInput
        name="text"
        label="Enter JSON to import requests."
        value={importedRequest}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setImportedRequest(e.target.value)
        }
        textarea
        style={{ height: "200px", width: "400px" }}
        required
        errors={
          !importedRequest
            ? { name: { type: "required", message: "Text is required" } }
            : undefined
        }
      />
    ),
    onSubmit: handleImportRequests,
  });

  const OverwriteConfirmation = useConfirmationModal({
    renderContent: () =>
      `A request with name ${duplicateRequests?.current[0]?.name} already exists. Do you want to overwrite the request?`,
    onSubmit: handleDuplicateNameRequests,
    title: "Duplicate request found",
  });

  const DeleteAllConfirmation = useConfirmationModal({
    renderContent: () => "Are you sure you want to delete all saved request?",
    onSubmit: handleDeleteAll,
    title: "Delete All",
    submitActionTheme: "danger",
  });

  const DeleteConfirmation = useConfirmationModal<NodeData>({
    renderContent: (node) => `Are you sure you want to delete "${node?.name}"?`,
    onSubmit: handleDeleteFile,
    title: "Delete Request",
    submitActionTheme: "danger",
  });

  const DeleteFolderConfirmation = useConfirmationModal<NodeData>({
    renderContent: (node) =>
      `Are you sure you want to delete the folder "${node?.name}"?`,
    onSubmit: handleDeleteFolder,
    title: "Delete Folder",
    submitActionTheme: "danger",
  });

  React.useEffect(() => {
    if (duplicateRequests.current.length) {
      OverwriteConfirmation.openModal();
    } else {
      OverwriteConfirmation.closeModal();
    }
  }, [duplicateRequests.current, OverwriteConfirmation]);

  //#endregion

  if (error) {
    return (
      <CenterContent fillHeight={false}>
        <ErrorAlert error={error} />
      </CenterContent>
    );
  }

  const haveContextNode = !!contextMenuNode && !!contextMenuNode.node;
  const nodeIsDir = haveContextNode && !contextMenuNode?.node?.file;
  const nodeIsNotDir = haveContextNode && contextMenuNode?.node?.file;

  return (
    <FolderActionContext.Provider value={{ createFolder, retrieveNodes }}>
      {loading || nodes === null ? (
        <CenterContent>
          <Spinner size="lg" dataTestId="spinner" />
        </CenterContent>
      ) : (
        <ServicesFileExplorer
          key={sortOption}
          nodes={filteredNodes}
          sortNodes={handleSortNodes}
          onEntryContext={handleEntryContext}
          onEntryClick={handleClick}
          getFileIcon={getFileIcon}
          getFileName={getFileName}
          multiSelect
          values={selectedValues}
          onSelectionChange={handleSelectionChange}
          defaultFoldersOpen={foldersOpen}
          retrieveNodes={retrieveNodes}
        />
      )}

      <CreateFolderModal
        open={createFolderModalOpen}
        folderPath={folderPath}
        onCancel={closeCreateFolder}
        onSubmit={handleCreateFolder}
      />

      <FileMoveModal
        open={moveFileModalOpen}
        filePath={filePath}
        onClose={closeFileMoveModal}
        onSubmit={handleFileMove}
        nodes={nodes}
      />

      {ImportConfirmation.content}
      {OverwriteConfirmation.content}
      {DeleteAllConfirmation.content}
      {DeleteConfirmation.content}
      {DeleteFolderConfirmation.content}

      <PositionedPortal
        open={contextMenuOpen}
        setPlacement={handleContextMenuPlacement}
        onClickOutside={closeContextMenu}
      >
        <Menu selectedKeys={[sortOption]} onClick={closeContextMenu}>
          {nodeIsNotDir &&
          !allNodesSelected &&
          !selectedNodes.current.length ? (
            <MenuItem
              key="delete-file"
              onClick={() => {
                DeleteConfirmation.openModal(contextMenuNode?.node);
              }}
            >
              <StyledIcon icon={faTrash} size="sm" />
              Delete File
            </MenuItem>
          ) : null}

          {nodeIsNotDir &&
          !allNodesSelected &&
          !selectedNodes.current.length ? (
            <MenuItem key="move-file" onClick={openFileMoveModal}>
              <StyledIcon icon={faFileImport} size="sm" />
              Move File
            </MenuItem>
          ) : null}
          {nodeIsDir && !allNodesSelected && !selectedNodes.current.length ? (
            <MenuItem
              key="delete-folder"
              onClick={() => {
                DeleteFolderConfirmation.openModal(contextMenuNode?.node);
              }}
            >
              <StyledIcon icon={faTrash} size="sm" />
              Delete Folder
            </MenuItem>
          ) : null}
          {haveContextNode && nodeIsNotDir ? (
            <>
              <MenuItem key="share-file" onClick={handleShareFile}>
                <StyledIcon icon={faShare} size="sm" />
                {selectedNodes.current.length
                  ? "Export Selected Files"
                  : "Export File"}
              </MenuItem>
            </>
          ) : null}
          {haveContextNode ? (
            <>
              <MenuItem
                key="delete-all"
                onClick={() => {
                  DeleteAllConfirmation.openModal();
                }}
              >
                <StyledIcon icon={faTrash} size="sm" />
                Delete All
              </MenuItem>
            </>
          ) : null}
          {selectedNodes.current.length ? (
            <>
              <MenuItem
                key="clear-selected-files"
                onClick={handleClearSelectedFiles}
              >
                <StyledIcon icon={faBroom} size="sm" />
                Clear All
              </MenuItem>
            </>
          ) : null}
          {haveContextNode || haveSelectedNodes ? <Divider /> : null}
          <SubMenu key="sort" title="Sort">
            <MenuItem key="Name" onClick={sortHandler("Name")}>
              By Name
            </MenuItem>
            <MenuItem key="ModifiedAt" onClick={sortHandler("ModifiedAt")}>
              By Modified At
            </MenuItem>
            <MenuItem key="CreatedAt" onClick={sortHandler("CreatedAt")}>
              By Created At
            </MenuItem>
          </SubMenu>
        </Menu>
      </PositionedPortal>
    </FolderActionContext.Provider>
  );
});

export default DirectoryExplorer;
